import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button as ButtonShape, Spinner, CardFooter, CardTitle } from "reactstrap";
import Axios from 'axios';
import { Auth } from "aws-amplify";
import { useSelector } from "react-redux";
import Skeleton from 'react-loading-skeleton';
import Head from 'src/layout/head/Head';
import {
  Block,
} from "src/components/Component";
import { useHistory } from "react-router-dom";

import { PlusCircleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import ProviderOptions from '../payment-provider/components/provider-options';
import API_ROUTES from "src/config/api";
import { useLocation } from 'react-router-dom';

import 'react-loading-skeleton/dist/skeleton.css'

let globalSetupInProgress = false;

const PaymentProvider = ({ providerID, providerName }) => {
  const history = useHistory();

  const [loading, setLoading] = useState(true);

  const [providers, setProviders] = useState([]);
  const { organisation } = useSelector((state) => state);
  const [directDebitSetup, setDirectDebitSetup] = useState(false);
  const [loadingDD, setLoadingDD] = useState(false);

  const params = useLocation();
  const query = new URLSearchParams(params.search);
  const ddFound = query.get('in_progress') ? true : false;
  const crezcoID = query.get('user-id');

  const loadCrezcoOnboarding = () => {
    const addUrl = process.env.REACT_APP_API_ENV === 'production'
      ? `https://app.crezco.com/onboarding?partner_id=felloh&redirect_uri=${window.location.href}`
      : `https://app.sandbox.crezco.com/onboarding?partner_id=felloh-sandbox&redirect_uri=${window.location.href}`;

    window.location.href = addUrl;
  }

  const getProviders = useCallback(async () => {
    setLoading(true);

    if (!organisation?.organisation) {
      return null;
    }

    const user = await Auth.currentAuthenticatedUser();

    const result = await Axios(
      API_ROUTES.payment['organisation:providers'](organisation.organisation, providerID),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    const ddResult = await Axios(
      API_ROUTES.user['organisation:surcharging:get'](organisation.organisation),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setDirectDebitSetup(ddResult.data.data.direct_debit);
    setProviders(result.data.data);
    setLoading(false);
  }, [organisation.id]);

  const createDirectDebit = async (e) => {
    e.preventDefault();
    setLoadingDD(true);

    const user = await Auth.currentAuthenticatedUser();

    const response = await Axios(
      API_ROUTES.bank['direct-debit:create'](organisation.organisation),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setLoadingDD(false);
    window.location.href = response.data.data.url;
  }

  const setupCrezco = useCallback(async () => {
    if (globalSetupInProgress) return; // Exit if already in progress

    globalSetupInProgress = true; // Mark as in progress

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.crezco['account:setup'](),
      {
        method: 'post',
        data: {
          organisation: organisation.organisation,
          crezco_id: crezcoID,
        },
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    history.push('/organisation/integrations/crezco');
  }, []);

  useEffect(() => {
    if (crezcoID && !globalSetupInProgress) {
      setupCrezco();
    } else {
      getProviders();
    }
  }, [crezcoID, organisation.organisation, setupCrezco]);

  if (loading === false && directDebitSetup === false && ddFound) return (
    <div>
      <Head title="Organisation Surcharging" />
      <Block>
        <div className="border bg-green-50 border border-green-300 rounded-md">
          <div className="text-white overflow-hidden p-4">
            <div className="text-green-400 text-lg font-semibold">
              <p className="text-md text-green-700"><strong>Your direct debit mandate has been received!</strong></p>
              <p className="text-sm text-green-700">A member of our team has been alerted. It will take up to an hour for us to receive confirmation of the mandate, please check back later.</p>
            </div>
          </div>
        </div>
      </Block>
    </div>
  );

  if (loading === false && directDebitSetup === false) return (
    <div>
      <Head title="Crezco Integration" />
      <div className="flex items-center p-4 justify-center">
        <Card className="w-full max-w-2xl border border-violet-300 rounded-lg">
          <CardHeader className='bg-violet-50 p-4'>
            <CardTitle className="text-xl font-bold text-center mb-0 text-violet-800">Direct Debit Mandate Required</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4 p-8">
            <p className="text-gray-600 font-bold">
              A direct debit is required in order to use Crezco.
            </p>
            <p className="text-gray-600">
              Felloh charges <strong>0.15% (15 basis points)</strong> on each transaction for this facility, these charges will be invoiced and undertaken via direct debit on a month end basis.
            </p>
            <div class="flex-grow border-t border-gray-400" />
            <p className="text-violet-800 text-sm">
              <strong>Example: </strong>On a £1,000 transaction, You would be invoiced by Felloh for £1.50 for transaction processing.
            </p>
          </CardBody>
          <CardFooter className="flex justify-center">
            <ButtonShape color="primary" className="bg-violet-800" size="md" disabled={loadingDD} onClick={createDirectDebit}>
              <span>
                {loadingDD === true ? <Spinner size="sm" className="mt-1 mr-3"> </Spinner> : 'Agree & Setup Direct Debit'}</span>
            </ButtonShape>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  return (
    <React.Fragment>

      <Head title="Crezco Integration" />

      <Card className="flex flex-row w-full border border-violet-200 rounded-lg p-4 bg-violet-50">
        <div className="w-100 text-purple-900">
          <div className='float-left mr-3 my-1'>
            <InformationCircleIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-violet-900 text-sm">
              Felloh charges <strong>0.15% (15 basis points)</strong> on each transaction for this facility, these charges will be invoiced and undertaken via direct debit on a month end basis.
            </p>
            <p className="text-slate-800 text-xs">
              <strong>Example: </strong>On a £1,000 transaction, You would be invoiced by Felloh for £1.50 for transaction processing.
            </p>
          </div>
        </div>
      </Card>

      {!crezcoID && loading === false && providers.length > 0 && providers.map((provider, index) => ((
        <div className="my-4 grid gap-6 md:grid-cols-2">
          <Card className="relative overflow-hidden border-primary/10 border border-slate-300 rounded-lg">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-b from-primary/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />

            <CardHeader className="pb-0 bg-slate-50 clear-both overflow-hidden">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="font-normal text-lg text-primary">Crezco <strong>Connection</strong></CardTitle>
                </div>
                <div className={`-mr-3 flex items-center gap-1.5 ${provider.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} px-2.5 py-1 rounded-full text-xs font-medium`}>
                  <span className="relative flex h-2 w-2">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${provider.is_active ? 'bg-emerald-500' : `bg-red-500`} opacity-75`}></span>
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${provider.is_active ? 'bg-emerald-500' : `bg-red-500`}`}></span>
                  </span>
                  {provider.is_active ? 'Active ' : 'Deactivated'}
                </div>
              </div>
            </CardHeader>

            <CardBody className='pb-0 mb-0'>
              <div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-10 clear-both mx-1">
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Status</p>
                    <div className="flex items-center gap-1.5 text-emerald-700">
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>Connected</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-muted-foreground font-bold">Last Active</p>
                    <p className="font-medium">Just now</p>
                  </div>
                </div>

                <div className='clear-both flex w-100 mt-12 mb-4'>
                  <ProviderOptions fullWidth={true} unedit={true} item={provider} reload={() => getProviders(true)} />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="border-dashed border-2 border-muted-foreground/20 bg-muted/50 h-100 mt-0 cursor-pointer" onClick={() => history.push('/organisation/integrations')}>
            <CardBody className="flex flex-col items-center justify-center h-[100px] text-center p-6">
            <PlusCircleIcon className="h-8 w-8 text-muted-foreground mb-4 text-violet-800" />
            <h3 className="font-semibold text-muted-foreground text-violet-800">Add New Connection</h3>
              <p className="text-sm text-muted-foreground mt-1">Connect another payment provider</p>
            </CardBody>
          </Card>
        </div>
      )))}

      {!crezcoID && loading === false && providers.length === 0 && (
        <div className="my-4 w-100">
          <Card
            className="border-dashed border-2 border-muted-foreground/20 bg-muted/50 mt-0 cursor-pointer"
            onClick={loadCrezcoOnboarding}
          >
            <CardBody className="flex flex-col items-center justify-center h-[200px] text-center p-6">
              <PlusCircleIcon className="h-8 w-8 text-muted-foreground mb-4 text-violet-800" />
              <h3 className="font-semibold text-muted-foreground text-violet-800">Add New Connection</h3>
              <p className="text-sm text-muted-foreground mt-1">Connect your Felloh account to Crezco</p>
            </CardBody>
          </Card>
        </div>
      )}

      {!crezcoID && loading === true && (
        <div className="my-4 grid gap-6 md:grid-cols-2">

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

          <Skeleton count={1} height="200px" className="mb-2" style={{
            width: '100%',
          }} />

        </div>
      )}

      {crezcoID && (
        <div className="my-4 w-100">
          <Card
            className="border-dashed border-2 border-violet-200 border-muted-foreground/20 bg-muted/50 mt-0 bg-slate-50"
          >
            <CardBody className="flex flex-col items-center justify-center h-[200px] text-center p-6">
              <div className="font-semibold mb-4">
                <p className="text-lg font-bold text-purple-800"><strong>Your Crezco account has been connected!</strong></p>
                <p className="text-md text-slate-700">Please wait, we are configuring you account. This won't take long...</p>
              </div>
              <Spinner size="xl" className="mt-1 mr-3 text-purple-700"> </Spinner>
            </CardBody>
          </Card>
        </div>
      )}

    </React.Fragment>
  )
};

export default PaymentProvider;
